import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CaseApprovalQuote, QuoteApprover } from '../../api/case/case-approval-quote';
import { CaseService } from '../../services/data/case.service';
import { NgForm } from '@angular/forms';
import { touchForm } from '../../utils/touch-form';
import { NoteMessage } from '../../api/messaging/note-message';
import { UserService } from '../../services/data/user.service';
import { MessageCategory } from '../../api/messaging/message-category';
import { MessageType } from '../../api/messaging/message-type';
import { HttpParams } from '@angular/common/http';
import { clone } from '../../utils/clone';
import { AdsCaseStatus } from '../../api/case/ads-case-status';

export interface ApproverDetails {
  name?: string;
  email?: string;
  note?: string;
  confirmDetails?: boolean;
}

enum APPROVALSTATUS {
  PENDING_APPROVAL,
  APPROVED,
  REVISION,
  ON_HOLD
}

@Component({
    selector: 'app-quote-approval',
    templateUrl: './quote-approval.component.html',
    styleUrls: ['./quote-approval.component.scss'],
    standalone: false
})
export class QuoteApprovalComponent implements OnInit {

  public token: string;

  public caseNumber: string;

  public case: CaseApprovalQuote;

  public approverDetails: ApproverDetails = {};

  public approverEmail: string;

  public hasExistingAccount: boolean;

  public status: APPROVALSTATUS;

  public approvalStatus = APPROVALSTATUS;

  public adsCaseStatus = AdsCaseStatus;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private caseService: CaseService,
    private userService: UserService,
  ) { }

  public async ngOnInit(): Promise<void> {
    this.token = this.route.snapshot.queryParamMap.get('token');

    this.caseNumber = this.getParamValue('caseNumber');
    // Email addresses never contain a literal space; a space here means the "+" (e.g. proton "+1" aliasing)
    // was already mangled to a space upstream before this link's query string was built.
    this.approverEmail = this.getParamValue('email')?.replace(/ /g, '+');

    this.case = await this.caseService
      .getByCaseNumber(this.caseNumber)
      .toPromise();

    this.updateStatus();

    this.hasExistingAccount = !(await this.userService
      .isOffline(this.approverEmail)
      .toPromise());
  }

  public async approve(form: NgForm): Promise<void> {
    touchForm(form);
    if (form.invalid) {
      return;
    }

    this.case.approver = {
      name: this.approverDetails.name,
      email: this.approverDetails.email,
    } as QuoteApprover;

    this.case.approvalNote = {
      messageBody: this.approverDetails.note,
      category: MessageCategory.Approval,
      messageType: MessageType.Note
    } as NoteMessage;

    this.case.revisionNote = null;

    try {
      this.case = await this.caseService.approveQuote(this.case, this.token)
        .toPromise();
      this.updateStatus();
    } catch (e) {
      if (e.status === 409) {
        this.status = APPROVALSTATUS.ON_HOLD;
      }
    }
  }

  public navigateToLogin(): void {
    this.router.navigate([`park-ads/company/case/${this.case.caseId}/tracker`]);
  }

  private getParamValue(param: string): string {
    const search = clone(window.location.search);
    const cleanSearch = search.split('amp;')
      .join('');
    const params = new HttpParams({fromString: cleanSearch.split('?')[1]});
    return params.get(param);
  }

  private updateStatus(): void {
    const caseStatus = this.case.status;
    if (caseStatus === AdsCaseStatus.NeedsReview || caseStatus === AdsCaseStatus.RequiresQuote) {
      this.status = APPROVALSTATUS.REVISION;
    } else if (caseStatus === AdsCaseStatus.QuotePendingApproval) {
      this.status = APPROVALSTATUS.PENDING_APPROVAL;
    } else if (caseStatus === AdsCaseStatus.OnHold) {
      this.status = APPROVALSTATUS.ON_HOLD;
    } else {
      this.status = APPROVALSTATUS.APPROVED;
    }
  }
}
